<script setup>
// shadcn-style badge. `variant`: secondary (grey), outline, or a colour
// tone for categories that must be told apart at a glance.
defineProps({
  variant: {
    type: String,
    default: 'secondary',
    validator: (v) =>
      ['secondary', 'outline', 'dashed', 'gray', 'blue', 'violet', 'green', 'amber', 'red'].includes(v),
  },
});
</script>

<template>
  <span class="ui-badge" :class="`ui-badge--${variant}`"><slot /></span>
</template>

<style scoped>
.ui-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  padding: 2px 8px;
  border: 1px solid transparent;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ui-badge--secondary {
  background-color: var(--accent);
  color: var(--foreground);
}
.ui-badge--outline {
  border-color: var(--border);
  color: var(--foreground);
}
.ui-badge--dashed {
  border: 1px dashed var(--border-strong);
  color: var(--muted-foreground);
}
.ui-badge--gray {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  color: #334155;
}
.ui-badge--blue {
  background-color: #eff6ff;
  border-color: #bfdbfe;
  color: #1d4ed8;
}
.ui-badge--violet {
  background-color: #f5f3ff;
  border-color: #ddd6fe;
  color: #6d28d9;
}
.ui-badge--green {
  background-color: #ecfdf5;
  border-color: #a7f3d0;
  color: #047857;
}
.ui-badge--amber {
  background-color: #fffbeb;
  border-color: #fde68a;
  color: #b45309;
}
.ui-badge--red {
  background-color: #fef2f2;
  border-color: #fecaca;
  color: #b91c1c;
}

/* Dark mode: translucent tints with light text */
html.dark .ui-badge--gray {
  background-color: rgba(148, 163, 184, 0.15);
  border-color: rgba(148, 163, 184, 0.35);
  color: #cbd5e1;
}
html.dark .ui-badge--blue {
  background-color: rgba(59, 130, 246, 0.15);
  border-color: rgba(59, 130, 246, 0.3);
  color: #93c5fd;
}
html.dark .ui-badge--violet {
  background-color: rgba(139, 92, 246, 0.15);
  border-color: rgba(139, 92, 246, 0.3);
  color: #c4b5fd;
}
html.dark .ui-badge--green {
  background-color: rgba(16, 185, 129, 0.15);
  border-color: rgba(16, 185, 129, 0.3);
  color: #6ee7b7;
}
html.dark .ui-badge--amber {
  background-color: rgba(245, 158, 11, 0.15);
  border-color: rgba(245, 158, 11, 0.3);
  color: #fcd34d;
}
html.dark .ui-badge--red {
  background-color: rgba(239, 68, 68, 0.15);
  border-color: rgba(239, 68, 68, 0.3);
  color: #fca5a5;
}
</style>
